import cn from 'classnames';
import { partition } from 'lodash';
import React, { useEffect } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { useSelector } from 'react-redux';

import { SalesByChannelsPie } from '../../graphs/SalesByChannelsPie';
import { AnalyticsItemBox } from './AnalyticsItemBox';
import { analyticsTopSellersUtils } from '../../../utils/analytics/top-sellers.utils';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';

import { AnalyticsOrderType } from '../../../constants/analytics';
import { pieChartGraphData } from '../../graphs/utils';
import { MAX_DISPLAYED_RETAILERS } from '../../graphs/consts';
import { REPORT_TYPES } from '../../tables/reports/consts';

import styles from '../../graphs/graph.module.scss';

export function SalesByChannelsPieContainer() {
  const {
    currentOrderType,
    currentCurrency,
    retailerNamesByIds,
    metricsDataType: { 'sales-by-channels': currentMetric },
    ui: {
      loading: { [REPORT_TYPES.SALES_BY_DAY]: dataLoading, canLoadData },
    },
    additionalData: {
      [REPORT_TYPES.SALES_BY_DAY]: { data },
    },
  } = useSelector((state) => state.analytics);

  const mixpanel = useMixpanel();

  useEffect(() => {
    reportBusinessActionToMixpanel(mixpanel, 'sales-dashboard-sales-by-channels-graph', {
      chosenValue: currentMetric,
    });
  }, [currentMetric, mixpanel]);

  const isGmvMetric = currentOrderType === AnalyticsOrderType.SALES;
  const dataKey = analyticsTopSellersUtils.getDataKeyFromCurrentOrderType(currentOrderType);

  const graphData = pieChartGraphData(data, retailerNamesByIds, currentCurrency);
  graphData.sort(({ gmv: gmvA, units: unitsA }, { gmv: gmvB, units: unitsB }) =>
    isGmvMetric ? gmvB.value - gmvA.value : unitsB.value - unitsA.value,
  );

  const uiData = {
    gmv: graphData.slice(0, MAX_DISPLAYED_RETAILERS).map((datum) => datum.gmv),
    units: graphData.slice(0, MAX_DISPLAYED_RETAILERS).map((datum) => datum.units),
    orders: graphData.slice(0, MAX_DISPLAYED_RETAILERS).map((datum) => datum.orders),
  };

  let otherGmvValue = graphData.slice(MAX_DISPLAYED_RETAILERS).reduce((sum, datum) => sum + datum.gmv.value, 0) || 0;
  let otherUnitsValue = graphData.slice(MAX_DISPLAYED_RETAILERS).reduce((sum, datum) => sum + datum.units.value, 0);
  let otherOrdersValue = graphData.slice(MAX_DISPLAYED_RETAILERS).reduce((sum, datum) => sum + datum.orders.value, 0);
  let otherGmvTotal =
    graphData.slice(MAX_DISPLAYED_RETAILERS).reduce((sum, datum) => sum + Number(datum.gmv.total), 0) || 0;
  let otherUnitsTotal = graphData
    .slice(MAX_DISPLAYED_RETAILERS)
    .reduce((sum, datum) => sum + Number(datum.units.total), 0);

  let otherOrdersTotal = graphData
    .slice(MAX_DISPLAYED_RETAILERS)
    .reduce((sum, datum) => sum + Number(datum.orders.total), 0);

  const [gmvLargerThanOne, gmvSmallerThanOne] = partition(uiData.gmv, (datum) => datum.value > 1);
  const [unitsLargerThanOne, unitsSmallerThanOne] = partition(uiData.units, (datum) => datum.value > 1);
  const [ordersLargerThanOne, ordersSmallerThanOne] = partition(uiData?.orders || [], (datum) => datum?.value > 1);

  uiData.gmv = gmvLargerThanOne;
  uiData.units = unitsLargerThanOne;
  uiData.orders = ordersLargerThanOne;

  if (gmvSmallerThanOne.length) {
    otherGmvValue += gmvSmallerThanOne.reduce((sum, datum) => sum + datum.value, 0);
    otherGmvTotal += gmvSmallerThanOne.reduce((sum, datum) => sum + datum.total, 0);
  }
  if (unitsSmallerThanOne.length) {
    otherUnitsValue += unitsSmallerThanOne.reduce((sum, datum) => sum + datum.value, 0);
    otherUnitsTotal += unitsSmallerThanOne.reduce((sum, datum) => sum + datum.total, 0);
  }

  if (ordersSmallerThanOne.length) {
    otherOrdersValue += ordersSmallerThanOne.reduce((sum, datum) => sum + datum?.value, 0);
    otherOrdersTotal += ordersSmallerThanOne.reduce((sum, datum) => sum + datum?.total, 0);
  }

  if (otherGmvValue) {
    uiData.gmv.push({
      id: 'Other',
      value: Number(otherGmvValue.toFixed(2)),
      total: Number(otherGmvTotal.toFixed(2)),
    });
  }

  if (otherUnitsValue) {
    uiData.units.push({
      id: 'Other',
      value: Number(otherUnitsValue.toFixed(2)),
      total: Number(otherUnitsTotal.toFixed(2)),
    });
  }

  if (otherOrdersValue) {
    uiData.orders.push({
      id: 'Other',
      value: Number(otherOrdersValue.toFixed(2)),
      total: Number(otherOrdersTotal.toFixed(2)),
    });
  }

  if (dataLoading || !canLoadData) {
    return (
      <AnalyticsItemBox title="Sales by channels" largeSize showLoader={true}>
        <div className={cn(styles.graphWrapper, styles.pieGraph)} />
      </AnalyticsItemBox>
    );
  }

  return (
    <SalesByChannelsPie
      title="Sales by channels"
      data={uiData}
      uiDataKey={dataKey}
      metric="sales-by-channels"
      isGmvMetric={isGmvMetric}
      currentOrderType={currentOrderType}
      currency={currentCurrency}
    />
  );
}
